import {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import Phaser from "phaser";
import PreloadScene from "./phaser/PreloadScene";
import GameScene from "./phaser/GameScene";

// Module-level state to survive StrictMode unmount/remount cycle
let pendingDestroy = null;
let cachedGame = null;
let cachedGameScene = null;
let cachedInitTimeout = null;
let cachedPreloadDone = false;
let cachedGameSceneStarted = false;
let cachedInitialQuestions = null; // questions snapshot at game creation time

const PhaserGame = forwardRef(function PhaserGame(
  {
    questions,
    onAnswer,
    onAnimatingChange,
    onQuestionStart,
    onBankControl,
    assetsUrl,
    soundEnabled = true,
    randomizeQuestions = false,
    randomizeAnswers = true,
    started = false,
  },
  ref,
) {
  const containerRef = useRef(null);
  const gameRef = useRef(null);
  const gameSceneRef = useRef(null);

  // Store config in ref - UPDATE this every render to prevent stale closures
  const configRef = useRef({
    questions,
    onAnswer,
    onAnimatingChange,
    onQuestionStart,
    onBankControl,
    assetsUrl,
    soundEnabled,
    randomizeQuestions,
    randomizeAnswers,
    started,
  });

  // Update ref on every render so it always has current values
  useEffect(() => {
    configRef.current = {
      questions,
      onAnswer,
      onAnimatingChange,
      onQuestionStart,
      onBankControl,
      assetsUrl,
      soundEnabled,
      randomizeQuestions,
      randomizeAnswers,
      started,
    };
  });

  // Start GameScene only when BOTH preload is done AND questions are final.
  // If randomizeQuestions=true, "final" means startGame already shuffled them
  // (detected by questions changing from the initial snapshot at game creation).
  const tryStartGameScene = useCallback(() => {
    if (!cachedPreloadDone || cachedGameSceneStarted || !cachedGame) return;
    const config = configRef.current;
    if (!config.started) return;
    if (config.randomizeQuestions && config.questions === cachedInitialQuestions) return;
    cachedGameSceneStarted = true;
    cachedGame.scene.start("GameScene", {
      questions: config.questions,
      onAnswer: config.onAnswer,
      onAnimatingChange: config.onAnimatingChange,
      onQuestionStart: config.onQuestionStart,
      onBankControl: config.onBankControl,
      soundEnabled: config.soundEnabled,
      randomizeQuestions: config.randomizeQuestions,
      randomizeAnswers: config.randomizeAnswers,
    });
  }, []);

  useImperativeHandle(ref, () => ({
    // Method to trigger timeout from React
    triggerTimeOut: () => {
      if (gameSceneRef.current) {
        gameSceneRef.current.triggerTimeOut();
      }
    },
    // Method to signal game-over (lives depleted) from React
    setGameOver: (onComplete) => {
      if (gameSceneRef.current) {
        gameSceneRef.current.setGameOver(onComplete);
      } else if (onComplete) {
        onComplete();
      }
    },
    // Method to get current scene
    getScene: () => gameSceneRef.current,
    // Method to destroy game
    destroy: () => {
      if (cachedInitTimeout) {
        clearTimeout(cachedInitTimeout);
        cachedInitTimeout = null;
      }
      if (pendingDestroy) {
        clearTimeout(pendingDestroy);
        pendingDestroy = null;
      }
      if (gameRef.current) {
        gameRef.current.destroy(true);
        gameRef.current = null;
        cachedGame = null;
        cachedGameScene = null;
      }
    },
  }));

  const initializeGame = useCallback(() => {
    if (!containerRef.current) return;

    // Guard: container must have valid dimensions to avoid 0×0 WebGL framebuffer error
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      // Container not ready yet — wait for next attempt
      return;
    }

    // StrictMode remount: cancel pending destruction and reuse cached instance
    if (pendingDestroy) {
      clearTimeout(pendingDestroy);
      pendingDestroy = null;
    }

    if (cachedGame) {
      // Reuse existing Phaser instance - re-parent the canvas
      const canvas = cachedGame.canvas;
      if (canvas && containerRef.current && !containerRef.current.contains(canvas)) {
        containerRef.current.appendChild(canvas);
      }
      gameRef.current = cachedGame;
      gameSceneRef.current = cachedGameScene;
      // Sync flags with actual game state on StrictMode remount
      cachedPreloadDone = true;
      cachedGameSceneStarted = cachedGame.scene.isActive("GameScene");
      return;
    }

    if (gameRef.current) return;

    // Save questions at creation time — used to detect when startGame shuffles them
    cachedInitialQuestions = configRef.current.questions;

    // Create custom scenes with data
    const preloadScene = new PreloadScene();
    const gameScene = new GameScene();

    // Store reference to game scene
    gameSceneRef.current = gameScene;
    cachedGameScene = gameScene;

    const phaserConfig = {
      type: Phaser.AUTO,
      parent: containerRef.current,
      backgroundColor: "transparent",
      transparent: true,
      scale: {
        mode: Phaser.Scale.RESIZE,
        autoCenter: Phaser.Scale.CENTER_BOTH,
        width: "100%",
        height: "100%",
      },
      // Don't auto-start scenes, we'll add them manually
      scene: [],
      physics: {
        default: "arcade",
        arcade: {
          gravity: { y: 0 },
          debug: false,
        },
      },
      pipeline: [],
      dom: {
        createContainer: true,
      },
      audio: {
        disableWebAudio: false,
      },
    };

    // Create Phaser game
    const game = new Phaser.Game(phaserConfig);
    gameRef.current = game;
    cachedGame = game;

    // Guard: si el contenedor no está en el DOM o tiene dimensiones 0
    // (transiciones a score screen, modales, tab en background, display:none
    // en el padre, etc.), el ScaleManager emite resize con 0x0 y WebGL falla
    // al crear el framebuffer ("Framebuffer status: Incomplete Attachment").
    // Interceptamos refresh() para saltar ese frame y reintentar en el siguiente.
    const scaleManager = game.scale;
    const originalRefresh = scaleManager.refresh.bind(scaleManager);
    scaleManager.refresh = function (...args) {
      const parent = containerRef.current;
      if (!parent || !parent.isConnected) return scaleManager;
      const rect = parent.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return scaleManager;
      return originalRefresh(...args);
    };

    // Add scenes manually after a short delay to ensure game is ready
    // Use module-level timeout so it survives StrictMode cleanup
    cachedInitTimeout = setTimeout(() => {
      // Verify this game instance is still the active one
      if (cachedGame !== game) return;

      // Add PreloadScene and start it
      game.scene.add("PreloadScene", preloadScene, false);
      // Add GameScene but don't start it yet
      game.scene.add("GameScene", gameScene, false);

      // Start preload with config
      game.scene.start("PreloadScene", {
        assetsUrl: configRef.current.assetsUrl,
        onProgress: () => {
          // Loading progress
        },
        onComplete: () => {
          // Preload done — don't start GameScene yet if questions aren't final
          if (cachedGame !== game) return;
          game.scene.stop("PreloadScene");
          cachedPreloadDone = true;
          tryStartGameScene();
        },
      });
    }, 100);
  }, [tryStartGameScene]);

  useEffect(() => {
    initializeGame();

    // Watch for container size changes to retry initialization if dimensions appear
    let observer;
    if (containerRef.current && !cachedGame) {
      observer = new ResizeObserver(() => {
        initializeGame();
      });
      observer.observe(containerRef.current);
    }

    // Cleanup: defer destruction to allow StrictMode remount to reuse instance
    return () => {
      if (observer) {
        observer.disconnect();
      }
      gameRef.current = null;
      gameSceneRef.current = null;

      // Defer actual destruction - if remounted quickly (StrictMode), it will be cancelled
      pendingDestroy = setTimeout(() => {
        if (cachedInitTimeout) {
          clearTimeout(cachedInitTimeout);
          cachedInitTimeout = null;
        }
        if (cachedGame) {
          cachedGame.destroy(true);
          cachedGame = null;
          cachedGameScene = null;
          cachedPreloadDone = false;
          cachedGameSceneStarted = false;
          cachedInitialQuestions = null;
        }
        pendingDestroy = null;
      }, 100);
    };
  }, [initializeGame]);

  // Update game scene when questions change; also try to start GameScene if deferred
  useEffect(() => {
    if (gameSceneRef.current && questions) {
      gameSceneRef.current.questions = questions;
    }
    // questions changing = startGame was dispatched (shuffle done) → unblock deferred start
    tryStartGameScene();
  }, [questions, tryStartGameScene]);

  // Try to start GameScene when game has been started (user clicked Play)
  useEffect(() => {
    tryStartGameScene();
  }, [started, tryStartGameScene]);

  // Update callbacks when they change
  // This is the ONLY place where callbacks should be assigned to avoid stale closures
  useEffect(() => {
    if (gameSceneRef.current) {
      gameSceneRef.current.onAnswer = onAnswer;
      gameSceneRef.current.onAnimatingChange = onAnimatingChange;
      gameSceneRef.current.onQuestionStart = onQuestionStart;
      gameSceneRef.current.onBankControl = onBankControl;
    }
  }, [onAnswer, onAnimatingChange, onQuestionStart, onBankControl]);

  return <div ref={containerRef} style={{ width: "100%", height: "100%" }} />;
});

export default PhaserGame;
